import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api, { formatDate, formatCurrency } from '../lib/api';
import { useAuth } from '../lib/auth';
import { StatusBadge } from '../components/StatusBadge';
import AssetIntelligenceTab from '../components/AssetIntelligenceTab';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  ArrowLeft, Box, Copy, ClipboardList, AlertTriangle, Calendar, 
  Package, Clock, Wrench, Plus, ChevronRight, BarChart3, Info
} from 'lucide-react';
import { toast } from 'sonner';

export default function AssetDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isRTL = i18n.language === 'ar';
  
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => { loadAssetDetail(); }, [id]);

  const loadAssetDetail = async () => {
    try {
      const { data } = await api.get(`/assets/${id}/detail`);
      setData(data);
    } catch (err) {
      toast.error(t('toast.error'));
      navigate('/rooms');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success(t('assetDetail.copied'));
  };

  const getTimelineIcon = (type) => {
    const icons = {
      work_order: ClipboardList,
      part_issued: Package,
    };
    return icons[type] || ClipboardList;
  };

  const getTimelineColor = (type, details) => {
    if (type === 'work_order') {
      return details?.wo_type === 'preventive' 
        ? 'text-blue-500 bg-blue-100 dark:bg-blue-900/30' 
        : 'text-brand bg-brand/10';
    }
    if (type === 'part_issued') {
      return 'text-orange-500 bg-orange-100 dark:bg-orange-900/30';
    }
    return 'text-muted-foreground bg-muted';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-muted-foreground">{t('common.loading')}</div>;
  }

  if (!data) return null;

  const { asset, kpis, timeline, common_issues } = data;

  return (
    <div data-testid="asset-detail-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/rooms?tab=assets')} className="shrink-0" data-testid="asset-back-btn">
          <ArrowLeft className={`w-5 h-5 ${isRTL ? 'rotate-180' : ''}`} />
        </Button>
        
        <div className="flex-1 space-y-3">
          <div className="flex flex-wrap items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
              <Box className="w-5 h-5 text-purple-600" />
            </div>
            <div>
              <h1 className="font-barlow font-bold text-2xl text-foreground tracking-tight">{asset.name}</h1>
              <p className="text-sm text-muted-foreground">{asset.category || t('common.uncategorized')}</p>
            </div>
          </div>
          
          {/* Serial/Code with Copy */}
          {asset.serial && (
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="font-mono text-sm px-3 py-1">
                {t('assetDetail.serial')}: {asset.serial}
              </Badge>
              <button 
                onClick={() => copyToClipboard(asset.serial)} 
                className="p-1.5 hover:bg-muted rounded-md transition-colors"
                title={t('assetDetail.copyId')}
              >
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          )}
          
          {/* Location Info */}
          <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
            <span>{asset.branch_name}</span>
            {asset.room_name && (
              <>
                <span>•</span>
                <span>{asset.room_name}</span>
              </>
            )}
          </div>
        </div>

        {/* Create WR Button */}
        <Button 
          onClick={() => navigate(`/work-requests?prefill_asset=${asset.id}&prefill_branch=${asset.branch_id}`)}
          className="bg-brand hover:bg-brand-hover text-brand-foreground shrink-0"
          data-testid="create-wr-btn"
        >
          <Plus className="w-4 h-4 me-2" />
          {t('assetDetail.createRequest')}
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="mb-6">
          <TabsTrigger value="overview" className="flex items-center gap-2" data-testid="tab-overview">
            <Info className="w-4 h-4" />
            {t('common.overview')}
          </TabsTrigger>
          <TabsTrigger value="intelligence" className="flex items-center gap-2" data-testid="tab-intelligence">
            <BarChart3 className="w-4 h-4" />
            {t('intelligence.title')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Column */}
        <div className="lg:col-span-2 space-y-6">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                    <ClipboardList className="w-5 h-5 text-blue-500" />
                  </div>
                  <div>
                    <p className="text-2xl font-barlow font-bold text-brand">{kpis.total_work_orders || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('assetDetail.totalWOs')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`bg-card border-border shadow-sm ${kpis.open_work_orders > 0 ? 'border-amber-200 dark:border-amber-900/50' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                    <Clock className="w-5 h-5 text-amber-500" />
                  </div>
                  <div>
                    <p className={`text-2xl font-barlow font-bold ${kpis.open_work_orders > 0 ? 'text-amber-500' : 'text-brand'}`}>{kpis.open_work_orders || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('assetDetail.openWOs')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className={`bg-card border-border shadow-sm ${kpis.overdue_work_orders > 0 ? 'border-rose-200 dark:border-rose-900/50' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-rose-500" />
                  </div>
                  <div>
                    <p className={`text-2xl font-barlow font-bold ${kpis.overdue_work_orders > 0 ? 'text-rose-500' : 'text-brand'}`}>{kpis.overdue_work_orders || 0}</p>
                    <p className="text-xs text-muted-foreground">{t('assetDetail.overdueWOs')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="bg-card border-border shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                    <Calendar className="w-5 h-5 text-purple-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{kpis.last_pm_date ? formatDate(kpis.last_pm_date) : '—'}</p>
                    <p className="text-xs text-muted-foreground">{t('assetDetail.lastPMDate')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Common Issues */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground flex items-center gap-2">
                <Wrench className="w-5 h-5 text-muted-foreground" />
                {t('assetDetail.commonIssues')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {common_issues.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">{t('assetDetail.noIssues')}</p>
              ) : (
                <div className="space-y-2">
                  {common_issues.map((issue, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                      <span className="text-sm text-foreground">{issue.cause}</span>
                      <Badge variant="secondary">{issue.count}x</Badge>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Asset Info */}
          <Card className="bg-card border-border shadow-sm">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground">{t('assetDetail.title')}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('common.name')}</p>
                  <p className="font-medium text-foreground mt-1">{asset.name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('assets.category')}</p>
                  <p className="font-medium text-foreground mt-1">{asset.category || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('assetDetail.serial')}</p>
                  <p className="font-mono font-medium text-foreground mt-1">{asset.serial || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('common.branch')}</p>
                  <p className="font-medium text-foreground mt-1">{asset.branch_name}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('common.room')}</p>
                  <p className="font-medium text-foreground mt-1">{asset.room_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">{t('common.created')}</p>
                  <p className="font-medium text-foreground mt-1">{formatDate(asset.created_at)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar - Timeline */}
        <div className="space-y-6">
          <Card className="bg-card border-border shadow-sm" data-testid="asset-timeline">
            <CardHeader className="pb-3">
              <CardTitle className="font-barlow text-lg text-foreground">{t('assetDetail.history')}</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[500px] overflow-y-auto">
                {timeline.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground text-sm px-4">{t('timeline.noActivity')}</div>
                ) : (
                  <div className="relative">
                    <div className={`absolute ${isRTL ? 'right-6' : 'left-6'} top-0 bottom-0 w-0.5 bg-border`} />
                    
                    {timeline.map((item, idx) => {
                      const Icon = getTimelineIcon(item.type);
                      const colorClass = getTimelineColor(item.type, item.details);
                      return (
                        <div 
                          key={idx} 
                          className={`relative px-4 py-3 hover:bg-muted/30 transition-colors ${item.type === 'work_order' ? 'cursor-pointer' : ''}`}
                          onClick={() => item.type === 'work_order' && navigate(`/work-orders/${item.details?.id}`)}
                        >
                          <div className="flex gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 z-10 ${colorClass}`}>
                              <Icon className="w-4 h-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              {item.type === 'work_order' && (
                                <>
                                  <div className="flex items-center gap-2">
                                    <Badge variant={item.details?.wo_type === 'preventive' ? 'default' : 'outline'} className="text-xs">
                                      {item.details?.wo_type === 'preventive' ? t('workOrder.preventive') : t('workOrder.corrective')}
                                    </Badge>
                                    <StatusBadge status={item.details?.status} />
                                  </div>
                                  {item.details?.technician_name && (
                                    <p className="text-xs text-muted-foreground mt-1">{item.details.technician_name}</p>
                                  )}
                                </>
                              )}
                              {item.type === 'part_issued' && (
                                <p className="text-sm text-foreground">
                                  {item.details?.part_name} ({item.details?.qty})
                                </p>
                              )}
                              <p className="text-xs text-muted-foreground mt-1">
                                {formatDate(item.timestamp)}
                              </p>
                            </div>
                            {item.type === 'work_order' && (
                              <ChevronRight className={`w-4 h-4 text-muted-foreground ${isRTL ? 'rotate-180' : ''}`} />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="intelligence">
          <AssetIntelligenceTab assetId={asset.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
